import DownloadIcon from "@mui/icons-material/Download";
import { Button, CircularProgress } from "@mui/material";
import { ButtonProps } from "@mui/material/Button";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useCheckForFile } from "hooks/useCheckForFile";
import { useState } from "react";
import { downloadFromUrls } from "util/download";

interface JobDownloadButtonProps extends Omit<ButtonProps, "onClick"> {
  downloadKeys: string[];
}

export function JobDownloadButton({ downloadKeys, ...buttonProps }: JobDownloadButtonProps) {
  const { fetchFile } = useCheckForFile();
  const alerts = useAlerts();
  // The handler runs long on multi-key jobs (a batch container-label job can
  // emit a key per container, and downloadFromUrls spaces the clicks a second
  // apart), so the button is disabled while a run is in flight — both as an
  // activity indicator and so a second click can't interleave a second run of
  // clicks that would cancel the first run's provisional navigations.
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    if (busy || downloadKeys.length === 0) {
      return;
    }
    setBusy(true);
    try {
      // Resolve every signed URL before clicking any of them, so a key that
      // fails to sign surfaces its error before any file has downloaded.
      const signedUrls: string[] = [];
      for (const key of downloadKeys) {
        const signedUrl = await fetchFile(key);
        if (!signedUrl) {
          throw new Error(`Failed to download file: ${key}`);
        }
        signedUrls.push(signedUrl);
      }
      await downloadFromUrls(signedUrls);
    } catch (error) {
      if (error instanceof Error) {
        alerts.error(error.message);
      } else {
        alerts.error("An unknown error occurred while downloading files");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      startIcon={busy ? <CircularProgress size={20} /> : <DownloadIcon />}
      variant="outlined"
      onClick={handleDownload}
      disabled={busy}
      {...buttonProps}
    >
      {busy ? "Downloading…" : "Download Output Files"}
    </Button>
  );
}
